import { pinBasedRedrawOverrideAtom } from "@design-lab/shared/lib/config";
import type { models } from "jiffy-api";
import { atom } from "jotai";

export type PinningSessionData = {
  sessionId: string;
  state: models.designLab.pinning.SessionState;
  summary: string | null;
};

export type CurrentPinningSession = {
  // Used as the key for pinningSessions atom lookups. Falls back to artworkId
  // when no DesignProjectPlacement exists (e.g. new placements in edit mode).
  designProjectPlacementId: string;
  artworkId: string;
  sessionId: string;
};

export type PinState = "draft" | "processing" | "ready" | "needs_clarification" | "unsupported";

export function isSessionFinished(
  state: models.designLab.pinning.SessionState | undefined
): boolean {
  return state === "completed" || state === "approved";
}

// bbox2d format: [x1, y1, x2, y2] where (x1,y1) is top-left and (x2,y2) is bottom-right
export type BoundingBox2D = {
  bbox2d: [number, number, number, number];
};

export type MaskObject = {
  maskUrl: string;
  numObjectsDetected: number;
  inputSize: [number, number]; // [width, height]
  maskSize: [number, number]; // [width, height]
};

export type AreaOfInterest = {
  type: "bounding_box" | "mask";
  objects: Array<BoundingBox2D | MaskObject>;
};
export type ClarificationOption = {
  label: string;
  value: string;
};

export type ClarificationStatus = "resolved" | "pending" | "asking";

export type Clarification = {
  type: string; // "color_shade", "font_selection", "font_color", "scope", etc.
  question: string;
  label?: string; // Optional label for submission format
  options: ClarificationOption[];
  status: ClarificationStatus;
  answer: string | null;
};

export type TextInfo = {
  value: string | null;
  fontFamily: string;
  color: string;
};

export type PinMessage = {
  id: string;
  role: "user" | "ai";
  content: string;
  timestamp: number;
  clarifications?: Clarification[]; // For multiple clarifications
  textInfo?: TextInfo; // Text info for font/color preview rendering
};

export type Pin = {
  id: string;
  coordinates: { x: number; y: number }; // Natural coordinates
  state: PinState;
  messages: PinMessage[];
  instruction?: string;
  areasOfInterest?: AreaOfInterest;
  segmentationMaskUrl?: string; // Internal URL for persisted mask (overrides maskUrl in areasOfInterest)
  highlightAreasEnabled?: boolean;
  thinkingText?: string;
  isNew?: boolean;
};

export const pinningModeAtom = atom<boolean>(false);

export type DesignLabPlacementKey = "front" | "back" | "frontLeftChest";

// Stores pinning sessions (active + completed) keyed by designProjectPlacementId
export const pinningSessionsAtom = atom<Record<string, PinningSessionData>>({});

// Derived atom: true if any session is active or completed (design services requested)
export const hasRequestedDesignServicesAtom = atom(get => {
  const sessions = get(pinningSessionsAtom);
  return Object.values(sessions).some(
    session =>
      session.state === "active" ||
      session.state === "generating_preview" ||
      session.state === "preview_ready" ||
      isSessionFinished(session.state)
  );
});

// Maps current project's placement keys to placement IDs (as strings)
export const designProjectPlacementIdsAtom = atom<Record<DesignLabPlacementKey, string | null>>({
  front: null,
  back: null,
  frontLeftChest: null
});

// The session currently open in pinning UI
export const currentPinningSessionAtom = atom<CurrentPinningSession | null>(null);

export const pinsAtom = atom<Pin[]>([]);
export const activePinIdAtom = atom<string | null>(null);
export const hoveredPinIdAtom = atom<string | null>(null);

// Controls visibility of pin markers on the canvas
export const showPinsAtom = atom<boolean>(true);

// Tracks where the hover originated from (panel or canvas)
export type HoveredPinSource = "panel" | "canvas" | null;
export const hoveredPinSourceAtom = atom<HoveredPinSource>(null);

// Maximum number of pins allowed per session
export const MAX_PINS = 20;

// Configurable pin limit for single-pin auto-complete flow
// Increase this when ready to support multi-pin auto-complete
export const SINGLE_PIN_LIMIT = 1;

export type PinningImageApproval = {
  pinId: string;
  imageUrl: string;
  sessionId: string;
  designProjectPlacementId: string;
  summary: string | null;
};

export const pinningImageApprovalAtom = atom<PinningImageApproval | null>(null);

// Tracks whether the generated preview image has finished loading on the artboard
export const pinningImageLoadedAtom = atom<boolean>(false);

// Tracks whether an AI image approval/save is in progress
export const pinningImageSavingAtom = atom<boolean>(false);

export type DesignProjectPlacementIdsByKey = Record<DesignLabPlacementKey, string | null>;
export type PinningSessionsByPlacementId = Record<string, PinningSessionData>;

export function getPinningSessionForPlacement(params: {
  placementKey: DesignLabPlacementKey;
  designProjectPlacementIds: DesignProjectPlacementIdsByKey;
  pinningSessions: PinningSessionsByPlacementId;
  artworkId?: string | undefined;
}): { designProjectPlacementId: string | null; session: PinningSessionData | undefined } {
  const { placementKey, designProjectPlacementIds, pinningSessions, artworkId } = params;
  const designProjectPlacementId = designProjectPlacementIds[placementKey] || null;
  const lookupKey = designProjectPlacementId || artworkId || null;
  const session = lookupKey ? pinningSessions[lookupKey] : undefined;
  return { designProjectPlacementId, session };
}

// Colors for pin markers
export const PIN_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#F59E0B", // Amber
  "#14B8A6" // Teal
];

// Mobile view state for pinning panel
export type PinningMobileView = "list" | "detail";
export const pinningMobileViewAtom = atom<PinningMobileView>("list");

// Mobile panel expanded state
export const pinningPanelExpandedAtom = atom<boolean>(false);

// Tracks pinning availability per artwork
export const pinningEnabledArtworksAtom = atom<Record<string, boolean>>({});
export const pinningFeedbackLastEditedPinIdAtom = atom<string | null>(null);

// Timestamp of first pin placement in current session (for escape hatch timer)
export const firstPinPlacedAtAtom = atom<number | null>(null);

// Escape hatch trigger type — first trigger wins, subsequent ones are ignored
export type EscapeHatchTriggerType = "error" | "slow_response" | "long_chat" | "timer";
export const escapeHatchTriggerAtom = atom<EscapeHatchTriggerType | null>(null);

// Tracks pin IDs that have experienced AI errors (for "pin deleted after error" trigger)
export const pinsWithErrorsAtom = atom<Set<string>>(new Set<string>());

// Stores the initial message typed before entering pinning mode (message-first flow)
export const pendingInitialMessageAtom = atom<string | null>(null);

// Signals that a pin reached "ready" state without generating an image (non-generative)
// Incremented to trigger session auto-completion in PinningArtboard
export const nonGenerativeReadyCounterAtom = atom<number>(0);

export { pinBasedRedrawOverrideAtom };
